/**
 * A single compact knockout box: two teams side by side (crest above a short code), the match
 * date below, and an optional FINAL / BRONZE-FINAL badge. Tapping navigates to the match hub.
 * Shared by the whole mirrored tree — the box looks identical wherever it sits.
 */

import React, { useCallback, useMemo } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { useTheme } from '../../../src/theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import type { BracketMatch } from '../../../src/knockout/types/knockoutBracket.types'
import { BracketTeamCell } from './BracketTeamCell'
import { getKnockoutMatchBoxStyles } from './KnockoutMatchBox.styles'

export type BracketBadge = 'final' | 'bronze'

interface KnockoutMatchBoxProps {
  match: BracketMatch
  /** Renders a coloured badge under the date (gold FINAL, blue BRONZE-FINAL). */
  badge?: BracketBadge
  testID?: string
}

export const KnockoutMatchBox: React.FC<KnockoutMatchBoxProps> = ({ match, badge, testID }) => {
  const theme = useTheme()
  const styles = useMemo(() => getKnockoutMatchBoxStyles(theme), [theme])
  const router = useRouter()

  const played = match.status === 'finished' || match.status === 'live'
  const tappable = match.href != null && match.href !== ''

  const handlePress = useCallback(() => {
    if (match.href != null && match.href !== '') router.push(match.href as never)
  }, [match.href, router])

  // Two-legged tie: each side's score is the AGGREGATE, and the legs explain how it got there
  // (AC-6.7). A World Cup match carries no `legs`, so its box is unchanged.
  const legsLabel = useMemo(() => {
    if (!Array.isArray(match.legs) || match.legs.length < 2) return null;
    const parts = match.legs
      .map((leg, i) => (leg.home == null || leg.away == null ? null : `L${i + 1} ${leg.home}-${leg.away}`))
      .filter((part): part is string => part != null);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [match.legs]);

  // The shootout lives on its own line rather than beside a side's score: a tie level on
  // aggregate is decided by it, so it is the answer to "who went through", not a footnote.
  const pensLabel = match.home.penaltyScore != null && match.away.penaltyScore != null
    ? `pens ${match.home.penaltyScore}-${match.away.penaltyScore}`
    : null;

  const body = (
    <>
      <View style={styles.teamsRow}>
        <BracketTeamCell slot={match.home} played={played} />
        <BracketTeamCell slot={match.away} played={played} />
      </View>
      {legsLabel != null ? <Text style={styles.legs}>{legsLabel}</Text> : null}
      {pensLabel != null ? <Text style={styles.pens}>{pensLabel}</Text> : null}
      {match.dateLabel != null && match.dateLabel !== '' ? (
        <Text style={styles.date}>{match.dateLabel}</Text>
      ) : null}
      {badge != null ? (
        <View style={[styles.badge, badge === 'final' ? styles.badgeFinal : styles.badgeBronze]}>
          <Text style={[styles.badgeText, badge === 'final' ? styles.badgeTextFinal : styles.badgeTextBronze]}>
            {badge === 'final' ? 'FINAL' : 'BRONZE-FINAL'}
          </Text>
        </View>
      ) : null}
    </>
  )

  if (tappable) {
    return (
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        testID={testID}
        style={({ pressed }) => [
          styles.box,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.pressed,
        ]}
      >
        {body}
      </Pressable>
    )
  }

  return (
    <View style={styles.box} testID={testID}>
      {body}
    </View>
  )
}
